import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import NotificationService from '../../services/notificationService';
import { getImageURL } from '../../utils/api';
import { getInitialsAvatar } from '../../utils/mediaUtils';

// Frozen (sticky) leading columns -- fixed pixel widths so each column's
// `left` offset can be computed as an exact running total. Narrower set is
// used under the `sm` breakpoint (see useIsNarrowViewport) to leave a bit
// more room for the actual schedule data on a phone screen; the real
// space-saver on mobile is showing only one selected day at a time (see
// the mobile date picker, replacing the This Week/Today toggle).
const COL_WIDTHS = {
  wide: { timeSlot: 110, floor: 110, location: 140, daySubcol: 150, remarks: 240, actions: 50 },
  narrow: { timeSlot: 72, floor: 60, location: 100, daySubcol: 110, remarks: 130, actions: 40 },
};
// Shown on the right edge of the last frozen column only while the table is
// scrolled horizontally, to signal that content is sliding underneath it.
const FROZEN_EDGE_SHADOW = '6px 0 10px -4px rgba(15, 23, 42, 0.35)';
// Thin divider marking the boundary between one Time Slot's row-group and
// the next, and between one day's column-group and the next -- distinct
// from the regular thin cell borders so the merged blocks read as blocks.
const GROUP_BORDER = '2px solid #94a3b8';
// Afternoon pattern styling — mirrors the backend TIME_SLOTS `pattern` field.
const PATTERN_META = {
  A: { badge: 'Pattern A', slotBg: 'bg-sky-50', cellBg: 'bg-sky-50/40', badgeCls: 'bg-sky-200 text-sky-800', emptyHint: 'text-sky-300', dotCls: 'bg-sky-400' },
  B: { badge: 'Pattern B', slotBg: 'bg-violet-50', cellBg: 'bg-violet-50/40', badgeCls: 'bg-violet-200 text-violet-800', emptyHint: 'text-violet-300', dotCls: 'bg-violet-400' },
};

// Matches Tailwind's `sm` breakpoint (640px) so the column-width switch
// lines up with any `sm:` classes used elsewhere on this page.
const NARROW_BREAKPOINT = 640;

function useIsNarrowViewport() {
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < NARROW_BREAKPOINT
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${NARROW_BREAKPOINT - 1}px)`);
    const handler = () => setIsNarrow(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isNarrow;
}

function cellKey(classroomId, slotIdx, dateStr) {
  return `${classroomId}-${slotIdx}-${dateStr}`;
}

// NOTE: deliberately NOT `date.toISOString().slice(0,10)` -- in a positive
// UTC-offset timezone (e.g. Sri Lanka, UTC+5:30) local midnight is still the
// previous day in UTC, so that round-trip silently shifts every date back
// by one. Building the string from local getters avoids that.
function toLocalYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function todayStr() {
  return toLocalYMD(new Date());
}

function fmtDayHeader(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(d);
}

function fmtWeekRange(days) {
  if (!days.length) return '';
  return `${fmtDayHeader(days[0].date)} – ${fmtDayHeader(days[days.length - 1].date)}`;
}

function addDaysToAnchor(anchor, days) {
  const d = new Date(`${anchor}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toLocalYMD(d);
}

function isWeekendDate(dateStr) {
  const day = new Date(`${dateStr}T00:00:00`).getDay();
  return day === 0 || day === 6;
}

// Status dot colors shown next to each Batch/Subject dropdown option --
// covers both batches.status ('not started'/'ongoing'/'delivery completed'/
// 'batch completed') and batch_subjects.status ('not started'/'ongoing'/'completed').
// 'not started'/'ongoing' are shared by both enums and mean the same thing in
// either context, so they share a color; the batch-only and subject-only
// "done" states each get their own distinct color so they're never confused.
const STATUS_DOT_CLASS = {
  'not started': 'bg-amber-400',
  ongoing: 'bg-blue-500',
  completed: 'bg-teal-500',
  'delivery completed': 'bg-indigo-500',
  'batch completed': 'bg-slate-500',
};

// Shown in the "Status Legend" modal opened from the top bar -- explains
// what each dot color in the Batch/Subject dropdowns and cells means.
const STATUS_LEGEND = [
  { status: 'not started', label: 'Not Started', description: 'The batch or subject is scheduled but hasn’t begun yet.', appliesTo: 'Batch & Subject' },
  { status: 'ongoing', label: 'Ongoing', description: 'Currently in progress.', appliesTo: 'Batch & Subject' },
  { status: 'completed', label: 'Completed', description: 'This subject has finished for this batch.', appliesTo: 'Subject only' },
  { status: 'delivery completed', label: 'Delivery Completed', description: 'All subjects for this batch have finished teaching.', appliesTo: 'Batch only' },
  { status: 'batch completed', label: 'Batch Completed', description: 'The entire batch (including results/certification) has concluded.', appliesTo: 'Batch only' },
];

function StatusDot({ status }) {
  if (!status) return null;
  return (
    <span
      title={status}
      className={`mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[status] || 'bg-slate-300'}`}
    />
  );
}

// Each class session is treated as 2 hours (matching the schedule's fixed
// time bands). Planned hours = subject's numberOfClasses * 2; actual =
// however many sessions have really been scheduled (across all time,
// backend-computed) * 2. Still-remaining time shows "+Xh" in green;
// scheduled beyond the plan shows "-Xh" (extra) in red.
const HOURS_PER_CLASS = 2;

function HoursBadge({ numberOfClasses, scheduledCount }) {
  if (numberOfClasses == null || scheduledCount == null) return null;
  const expectedHours = numberOfClasses * HOURS_PER_CLASS;
  const scheduledHours = scheduledCount * HOURS_PER_CLASS;
  const remaining = expectedHours - scheduledHours;
  return (
    <div className="mt-1 inline-flex overflow-hidden rounded border border-slate-200 text-[10px] font-bold leading-none">
      <span className="bg-slate-100 px-1.5 py-1 text-slate-600" title="Expected hours">{expectedHours}h</span>
      <span className="border-l border-slate-200 bg-sky-50 px-1.5 py-1 text-sky-700" title="Scheduled hours">{scheduledHours}h</span>
      <span
        className={`border-l border-slate-200 px-1.5 py-1 ${remaining > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
        title={remaining > 0 ? 'Remaining hours' : 'Extra hours'}
      >
        {remaining > 0 ? `+${remaining}` : `-${Math.abs(remaining)}`}h
      </span>
    </div>
  );
}

// Type-to-search dropdown for the editable Batch/Subject/Lecturer cells --
// a plain `<select>` has no real search, so this renders a text input plus a
// filtered option list instead. Closes itself on outside click or Escape.
function SearchableCellSelect({ options, value, onSelect, onClose, placeholder }) {
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  return (
    <div ref={containerRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        placeholder={placeholder || 'Type to search…'}
        className="w-full rounded border border-violet-300 bg-white px-1.5 py-1 text-xs text-slate-700 outline-none"
      />
      <ul className="absolute left-0 top-full z-50 mt-0.5 max-h-52 w-56 max-w-[min(14rem,85vw)] overflow-y-auto rounded border border-slate-200 bg-white text-xs shadow-lg">
        {filtered.length === 0 && <li className="px-2 py-1.5 text-slate-400">No matches</li>}
        {filtered.map((o) => (
          <li
            key={o.value}
            onClick={() => onSelect(o.value)}
            className={`flex cursor-pointer items-center whitespace-normal px-2 py-1.5 hover:bg-violet-50 ${
              String(o.value) === String(value) ? 'bg-violet-100 font-semibold' : ''
            }`}
          >
            <StatusDot status={o.status} />
            {o.label}
            <AvailabilityDot available={o.available} reason={o.availabilityReason} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Green/red dot showing whether a subject's lecturer is actually free at the
// specific classroom+date+time being scheduled (roster off/half-day, leave/
// unavailability, or already teaching something else at that exact time).
// Renders nothing when availability wasn't computed for this option (e.g.
// the Batch dropdown, which has no lecturer to check).
function AvailabilityDot({ available, reason }) {
  if (available === null || available === undefined) return null;
  return (
    <span
      title={available ? 'Lecturer is available at this time' : reason || 'Lecturer is not available at this time'}
      className={`ml-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${available ? 'bg-emerald-500' : 'bg-red-500'}`}
    />
  );
}

export default function ClassroomWeeklySchedule() {
  const isNarrow = useIsNarrowViewport();
  const [anchor, setAnchor] = useState(todayStr());
  const [showTodayOnly, setShowTodayOnly] = useState(false);
  // Phone-width only: the This Week/Today toggle + horizontal day-scrolling
  // is replaced by a single native date picker showing exactly one day at a
  // time (see the mobile toolbar below and visibleDays' isNarrow branch).
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [isHScrolled, setIsHScrolled] = useState(false);
  const scrollContainerRef = useRef(null);
  const anchorRef = useRef(anchor);
  useEffect(() => { anchorRef.current = anchor; }, [anchor]);

  const { timeSlot: TIME_SLOT_COL_WIDTH, floor: FLOOR_COL_WIDTH, location: LOCATION_COL_WIDTH,
    daySubcol: DAY_SUBCOL_WIDTH, remarks: REMARKS_COL_WIDTH, actions: ACTIONS_COL_WIDTH } =
    isNarrow ? COL_WIDTHS.narrow : COL_WIDTHS.wide;
  const FROZEN_COL_LEFT = {
    timeSlot: 0,
    floor: TIME_SLOT_COL_WIDTH,
    location: TIME_SLOT_COL_WIDTH + FLOOR_COL_WIDTH,
  };
  // On mobile the Time Slot/Floor/Location columns scroll away with the rest
  // of the table instead of staying pinned -- with only one day's columns
  // showing at a time (see the mobile date picker), freezing them just eats
  // screen width without much benefit.
  const stickyPos = isNarrow ? 'static' : 'sticky';

  // Google-Sheets-style live refresh: silently re-fetches the currently
  // viewed week whenever anyone (including this tab) saves/clears/duplicates
  // a session, so everyone watching the page sees changes without reloading.
  async function silentRefreshWeek() {
    try {
      const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/week', { params: { anchor: anchorRef.current } });
      setData(resp);
    } catch {
      // best-effort -- a manual refresh will pick up the latest state anyway
    }
  }

  // Editing: who's allowed to edit (super admin, or a course coordinator --
  // /classroom-schedule/batches determines this server-side from
  // coordinate_courses and returns canEdit=false with an empty batch list
  // for staff who coordinate zero courses), the batches they can pick from,
  // and per-cell local state (batch/subject selections save immediately to
  // class_schedules via POST /session -- cellOverrides is just the optimistic
  // "what does this cell show right now" cache on top of the server data).
  const [permissions, setPermissions] = useState({ canEdit: false, isSuperAdmin: false });
  const [editableBatches, setEditableBatches] = useState([]);
  const [batchSubjectsCache, setBatchSubjectsCache] = useState({});
  // Per-subject lecturer availability for the SPECIFIC classroom+date+slot
  // being scheduled -- separate from batchSubjectsCache (which is date-
  // independent) since the same batch's subjects can be available for one
  // slot and not another. Keyed by `${batchId}-${dateStr}-${slotIdx}`.
  const [subjectAvailabilityCache, setSubjectAvailabilityCache] = useState({});
  const [cellOverrides, setCellOverrides] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { key, column: 'batch'|'subject'|'lecturer' }
  const [loadingCell, setLoadingCell] = useState(null); // key of cell with a pending async op
  const [legendOpen, setLegendOpen] = useState(false);
  const [actionsMenuKey, setActionsMenuKey] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null); // { key, classroomId, slotIdx, batchSubjectId, label, dates: [] }
  const [historyModal, setHistoryModal] = useState(null); // { label, loading, entries }
  const [scheduleConfirm, setScheduleConfirm] = useState(null); // { key, classroomId, slotIdx, dateStr, batchSubjectId, batchName, subjectName, lecturerName, classroomName, slotLabel, loading, saving, warnings }
  const [requiredFixModal, setRequiredFixModal] = useState(null); // { key, classroomId, slotIdx, dateStr, batchId, batchSubjectId, batchName, subjectName, classroomName, slotLabel, missingLecturer, notStarted, staffIdInput, statusInput, saving }
  const [activeStaff, setActiveStaff] = useState([]);
  const [activeStaffLoaded, setActiveStaffLoaded] = useState(false);
  const [eventModal, setEventModal] = useState(null); // { classroomId, classroomName, dateStr, startTime, endTime, slotLabel }
  const [eventForm, setEventForm] = useState({ title: '', eventTypeId: null, staffIds: [], notes: '' });
  const [eventTypes, setEventTypes] = useState([]);
  const [savingEvent, setSavingEvent] = useState(false);
  const [staffEventAvailability, setStaffEventAvailability] = useState({}); // { [staffId]: { available, reason } }
  const [loadingStaffAvail, setLoadingStaffAvail] = useState(false);
  const staffAvailTimerRef = useRef(null);
  const [eventHistoryModal, setEventHistoryModal] = useState(null); // { label, loading, entries }

  async function ensureActiveStaffLoaded() {
    if (activeStaffLoaded) return;
    setActiveStaffLoaded(true);
    try {
      const { data: resp } = await axiosInstance.get('/admin/staff');
      setActiveStaff((resp.staff || []).filter((s) => s.loginStatus === 'active'));
    } catch {
      setActiveStaffLoaded(false);
    }
  }

  useEffect(() => {
    axiosInstance.get('/admin/other-events/event-types')
      .then(({ data: resp }) => setEventTypes(Array.isArray(resp) ? resp : (resp.eventTypes || [])))
      .catch(() => {});
  }, []);

  // Debounced fetch: re-runs whenever the event modal's date/start/end changes.
  useEffect(() => {
    if (!eventModal) return;
    const { dateStr, startTime, endTime } = eventModal;
    if (!dateStr || !startTime || !endTime) return;
    if (staffAvailTimerRef.current) clearTimeout(staffAvailTimerRef.current);
    staffAvailTimerRef.current = setTimeout(async () => {
      setLoadingStaffAvail(true);
      try {
        const { data } = await axiosInstance.get('/admin/classroom-schedule/staff-event-availability', {
          params: { date: dateStr, startTime, endTime },
        });
        setStaffEventAvailability(data.availability || {});
      } catch {
        // silently ignore — dots just won't show
      } finally {
        setLoadingStaffAvail(false);
      }
    }, 400);
    return () => clearTimeout(staffAvailTimerRef.current);
  }, [eventModal?.dateStr, eventModal?.startTime, eventModal?.endTime]);

  function openEventModal(classroom, slotDef, dateStr) {
    ensureActiveStaffLoaded();
    setStaffEventAvailability({});
    setEventForm({ title: '', eventTypeId: null, staffIds: [], notes: '' });
    setEventModal({
      eventId: null,
      classroomId: classroom.classroomId,
      classroomName: classroom.name,
      dateStr,
      startTime: slotDef?.start || '',
      endTime: slotDef?.end || '',
      slotLabel: slotDef?.label || '',
    });
  }

  async function openEditEventModal(ev, classroomName) {
    ensureActiveStaffLoaded();
    setStaffEventAvailability({});
    setEventForm({ title: '', eventTypeId: null, staffIds: [], notes: '' });
    setEventModal({
      eventId: ev.eventId,
      classroomId: ev.classroomId,
      classroomName,
      dateStr: ev.dateStr,
      startTime: ev.startTime ? ev.startTime.slice(0, 5) : '',
      endTime: ev.endTime ? ev.endTime.slice(0, 5) : '',
      slotLabel: ev.slotLabel || '',
    });
    try {
      const { data } = await axiosInstance.get(`/admin/other-events/${ev.eventId}`);
      const full = data.event;
      setEventForm({
        title: full.title || '',
        eventTypeId: full.eventTypeId || null,
        staffIds: (full.staff || []).map((s) => s.staffId),
        notes: full.notes || '',
      });
    } catch {
      toast.error('Failed to load event details');
    }
  }

  async function openEventHistoryModal(eventId, label) {
    setEventHistoryModal({ label, loading: true, entries: [] });
    try {
      const { data } = await axiosInstance.get(`/admin/other-events/${eventId}/history`);
      setEventHistoryModal({ label, loading: false, entries: data.history || [] });
    } catch {
      setEventHistoryModal({ label, loading: false, entries: [] });
    }
  }

  async function saveEvent() {
    if (!eventModal || !eventForm.title.trim() || !eventForm.eventTypeId) {
      toast.error('Title and Event Type are required');
      return;
    }
    if (eventModal.startTime >= eventModal.endTime) {
      toast.error('End time must be after start time');
      return;
    }
    setSavingEvent(true);
    const payload = {
      title: eventForm.title.trim(),
      eventTypeId: eventForm.eventTypeId,
      date: eventModal.dateStr,
      startTime: eventModal.startTime,
      endTime: eventModal.endTime,
      classroomId: eventModal.classroomId,
      staffIds: eventForm.staffIds,
      notes: eventForm.notes || null,
    };
    try {
      if (eventModal.eventId) {
        await axiosInstance.put(`/admin/other-events/${eventModal.eventId}`, payload);
        toast.success('Event updated');
      } else {
        await axiosInstance.post('/admin/other-events', payload);
        toast.success('Event added');
      }
      setEventModal(null);
      const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/week', { params: { anchor } });
      setData(resp);
    } catch (err) {
      toast.error(err?.response?.data?.message || (eventModal.eventId ? 'Failed to update event' : 'Failed to add event'));
    } finally {
      setSavingEvent(false);
    }
  }

  async function clearEvent(eventId) {
    const confirmed = await Swal.fire({
      title: 'Clear event?',
      text: 'This will permanently remove the event from this slot.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear it',
      confirmButtonColor: '#ef4444',
    });
    if (!confirmed.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/other-events/${eventId}`);
      toast.success('Event cleared');
      const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/week', { params: { anchor } });
      setData(resp);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to clear event');
    }
  }

  const handleTableScroll = () => {
    setIsHScrolled((scrollContainerRef.current?.scrollLeft || 0) > 0);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setAccessDenied(false);
        const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/week', { params: { anchor } });
        if (!cancelled) setData(resp);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setAccessDenied(true);
          setError(err?.response?.data?.message || 'This page is only available to Academic staff.');
        } else {
          setError(err?.response?.data?.message || 'Failed to load the schedule.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [anchor]);

  // Editing setup: figure out who can edit + which batches are pickable.
  // Best-effort -- if this fails, the page still works as a read-only viewer.
  useEffect(() => {
    (async () => {
      try {
        const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/batches');
        setPermissions({ canEdit: !!resp?.canEdit, isSuperAdmin: !!resp?.isSuperAdmin });
        setEditableBatches(Array.isArray(resp?.batches) ? resp.batches : []);
      } catch {
        // Editing is an enhancement on the read-only schedule -- leave it off.
      }
    })();
  }, []);

  async function ensureBatchSubjectsLoaded(batchId) {
    if (!batchId || batchSubjectsCache[batchId]) return;
    try {
      const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/batch-subjects', { params: { batchId } });
      setBatchSubjectsCache((prev) => ({ ...prev, [batchId]: resp.batchSubjects || [] }));
    } catch {
      setBatchSubjectsCache((prev) => ({ ...prev, [batchId]: [] }));
    }
  }

  async function ensureSubjectAvailabilityLoaded(batchId, dateStr, slotIdx, classroomId) {
    const cacheKey = `${classroomId}-${batchId}-${dateStr}-${slotIdx}`;
    if (!batchId || subjectAvailabilityCache[cacheKey]) return;
    try {
      const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/batch-subjects', {
        params: { batchId, date: dateStr, slotIdx, classroomId },
      });
      const map = {};
      (resp.batchSubjects || []).forEach((s) => {
        map[s.batchSubjectId] = { available: s.available, reason: s.unavailableReason };
      });
      setSubjectAvailabilityCache((prev) => ({ ...prev, [cacheKey]: map }));
    } catch {
      setSubjectAvailabilityCache((prev) => ({ ...prev, [cacheKey]: {} }));
    }
  }

  function getEffectiveCell(classroomId, slotIdx, dateStr, originalCell) {
    const override = cellOverrides[cellKey(classroomId, slotIdx, dateStr)];
    if (!override) return originalCell;
    return { ...(originalCell || {}), ...override };
  }

  function getBatchStatus(batchId) {
    return editableBatches.find((b) => b.batchId === batchId)?.status || null;
  }

  function getSubjectStatus(batchId, batchSubjectId) {
    return (batchSubjectsCache[batchId] || []).find((s) => s.batchSubjectId === batchSubjectId)?.status || null;
  }

  // editableBatches is already scoped server-side to courses this user
  // coordinates (or ALL batches for super admins) -- a batch missing from
  // that list belongs to a course someone else coordinates, so Clear/
  // Duplicate stay off for it; View History is still fine either way.
  function isBatchInMyScope(batchId) {
    return editableBatches.some((b) => b.batchId === batchId);
  }

  function handleBatchChange(key, batchIdRaw) {
    const batchId = batchIdRaw ? Number(batchIdRaw) : null;
    const batch = editableBatches.find((b) => b.batchId === batchId);
    setCellOverrides((prev) => ({
      ...prev,
      [key]: {
        batchId: batch ? batch.batchId : null,
        batchNo: batch ? batch.batchName : '',
        batchSubjectId: null,
        subject: '',
        staffId: null,
        lecturer: '',
        remarks: '',
      },
    }));
    setEditingCell(null);
    if (batch) ensureBatchSubjectsLoaded(batch.batchId);
  }

  // Picking a batch only sets a local cellOverrides entry (see handleBatchChange
  // above) -- nothing is written to class_schedules until a subject is also
  // chosen (handleSubjectChange, which POSTs /session). So a batch picked but
  // never followed by a subject is purely local/unsaved, and can be discarded
  // outright by dropping the override -- there's nothing on the server to undo.
  function clearPendingBatch(key) {
    setCellOverrides((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleSubjectCellClick(key, batchId, dateStr, slotIdx, classroomId) {
    // Availability loads in background — dropdown updates reactively when done.
    ensureSubjectAvailabilityLoaded(batchId, dateStr, slotIdx, classroomId);

    if (!batchSubjectsCache[batchId]) {
      setLoadingCell(key);
      try {
        await ensureBatchSubjectsLoaded(batchId);
      } finally {
        setLoadingCell(null);
      }
    }
    setEditingCell({ key, column: 'subject' });
  }

  async function saveSession({ key, classroomId, slotIdx, dateStr, batchSubjectId, warnings }) {
    setLoadingCell(key);
    try {
      const { data: saved } = await axiosInstance.post('/admin/classroom-schedule/session', {
        classroomId, date: dateStr, slotIdx, batchSubjectId, conflictWarnings: warnings || [],
      });
      setCellOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...saved } }));
      toast.success('Schedule saved');
      setScheduleConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save schedule');
      setScheduleConfirm((prev) => (prev && prev.key === key ? { ...prev, saving: false } : prev));
    } finally {
      setLoadingCell(null);
    }
  }

  // Checks for scheduling conflicts and either saves right away (none found --
  // the original workflow) or opens a confirm modal explaining why (roster
  // off/half-day, leave/unavailability, or the lecturer/room already booked
  // at this time) instead of saving silently.
  function checkConflictsAndSave({ key, classroomId, slotIdx, dateStr, batchId, batchSubjectId, classroomName, slotLabel }) {
    const payload = { key, classroomId, slotIdx, dateStr, batchSubjectId };
    setLoadingCell(key);

    axiosInstance
      .get('/admin/classroom-schedule/check-conflicts', { params: { classroomId, date: dateStr, slotIdx, batchSubjectId } })
      .then(({ data: resp }) => {
        setLoadingCell(null);
        const warnings = resp.warnings || [];
        if (!warnings.length) {
          saveSession(payload);
          return;
        }
        const subj = (batchSubjectsCache[batchId] || []).find((s) => s.batchSubjectId === batchSubjectId);
        const batch = editableBatches.find((b) => b.batchId === batchId);
        setScheduleConfirm({
          ...payload,
          batchName: batch?.batchName || '',
          subjectName: subj?.subjectShortName || '',
          lecturerName: subj?.staffName || '',
          classroomName,
          slotLabel,
          warnings,
          saving: false,
        });
      })
      .catch(() => {
        setLoadingCell(null);
        // Don't block scheduling just because the conflict check itself failed.
        toast.error('Could not check for conflicts — saving anyway');
        saveSession(payload);
      });
  }

  // Picking a subject first makes sure it's actually ready to be scheduled --
  // it needs an assigned lecturer and a status past "Not Started". If either
  // is missing, a modal lets the coordinator fix it right there before the
  // conflict check/save happens at all.
  function handleSubjectChange(key, classroomId, slotIdx, dateStr, batchId, batchSubjectIdRaw, classroomName, slotLabel) {
    const batchSubjectId = batchSubjectIdRaw ? Number(batchSubjectIdRaw) : null;
    setEditingCell(null);

    if (!batchSubjectId) {
      setCellOverrides((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), batchSubjectId: null, subject: '', staffId: null, lecturer: '', remarks: '' },
      }));
      return;
    }

    const subj = (batchSubjectsCache[batchId] || []).find((s) => s.batchSubjectId === batchSubjectId);
    const batch = editableBatches.find((b) => b.batchId === batchId);
    const missingLecturer = !subj?.staffId;
    const notStarted = subj?.status === 'not started';

    if (missingLecturer || notStarted) {
      ensureActiveStaffLoaded();
      setRequiredFixModal({
        key, classroomId, slotIdx, dateStr, batchId, batchSubjectId,
        batchName: batch?.batchName || '',
        subjectName: subj?.subjectShortName || '',
        classroomName, slotLabel,
        missingLecturer, notStarted,
        staffIdInput: subj?.staffId || '',
        statusInput: subj?.status || 'not started',
        saving: false,
      });
      return;
    }

    checkConflictsAndSave({ key, classroomId, slotIdx, dateStr, batchId, batchSubjectId, classroomName, slotLabel });
  }

  async function confirmRequiredFix() {
    if (!requiredFixModal) return;
    const { batchId, batchSubjectId, missingLecturer, notStarted, staffIdInput, statusInput } = requiredFixModal;
    if (missingLecturer && !staffIdInput) {
      toast.error('Please assign a lecturer');
      return;
    }
    if (notStarted && statusInput === 'not started') {
      toast.error('Please change the status to Ongoing');
      return;
    }

    setRequiredFixModal((prev) => (prev ? { ...prev, saving: true } : prev));
    try {
      const body = {};
      if (missingLecturer) body.staffId = Number(staffIdInput);
      if (notStarted) body.status = statusInput;
      const { data: updated } = await axiosInstance.patch(`/admin/classroom-schedule/batch-subjects/${batchSubjectId}`, body);

      setBatchSubjectsCache((prev) => ({
        ...prev,
        [batchId]: (prev[batchId] || []).map((s) =>
          s.batchSubjectId === batchSubjectId ? { ...s, staffId: updated.staffId, staffName: updated.staffName, status: updated.status } : s
        ),
      }));

      const { key, classroomId, slotIdx, dateStr, classroomName, slotLabel } = requiredFixModal;
      setRequiredFixModal(null);
      checkConflictsAndSave({ key, classroomId, slotIdx, dateStr, batchId, batchSubjectId, classroomName, slotLabel });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update');
      setRequiredFixModal((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  }

  function confirmSchedule() {
    if (!scheduleConfirm) return;
    setScheduleConfirm((prev) => (prev ? { ...prev, saving: true } : prev));
    saveSession(scheduleConfirm);
  }

  async function handleClearCell(key, classroomId, slotIdx, dateStr, label) {
    setActionsMenuKey(null);
    const result = await Swal.fire({
      title: 'Clear this session?',
      html: `<b>${label}</b> on ${dateStr} will be removed. This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, clear it',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete('/admin/classroom-schedule/session', { data: { classroomId, date: dateStr, slotIdx } });
      setCellOverrides((prev) => ({
        ...prev,
        [key]: { batchId: null, batchNo: '', batchSubjectId: null, subject: '', staffId: null, lecturer: '', remarks: '' },
      }));
      toast.success('Cleared');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to clear');
    }
  }

  async function openHistoryModal(label, classroomId, slotIdx, dateStr) {
    setActionsMenuKey(null);
    setHistoryModal({ label, loading: true, entries: [] });
    try {
      const { data: resp } = await axiosInstance.get('/admin/classroom-schedule/history', {
        params: { classroomId, slotIdx, date: dateStr },
      });
      setHistoryModal({ label, loading: false, entries: resp.history || [] });
    } catch {
      setHistoryModal({ label, loading: false, entries: [], error: 'Failed to load history' });
    }
  }

  function openDuplicateModal(label, classroomId, slotIdx, batchSubjectId) {
    setActionsMenuKey(null);
    setDuplicateModal({ label, classroomId, slotIdx, batchSubjectId, dates: [], pickerValue: '' });
  }

  function addDuplicateDate(dateStr) {
    if (!dateStr) return;
    setDuplicateModal((prev) =>
      prev && !prev.dates.includes(dateStr) ? { ...prev, dates: [...prev.dates, dateStr].sort(), pickerValue: '', results: undefined } : prev
    );
  }

  function removeDuplicateDate(dateStr) {
    setDuplicateModal((prev) => (prev ? { ...prev, dates: prev.dates.filter((d) => d !== dateStr), results: undefined } : prev));
  }

  async function saveDuplicate() {
    if (!duplicateModal || !duplicateModal.dates.length) return;
    const { classroomId, slotIdx, batchSubjectId, dates } = duplicateModal;
    setDuplicateModal((prev) => (prev ? { ...prev, saving: true } : prev));
    try {
      const { data: resp } = await axiosInstance.post('/admin/classroom-schedule/duplicate', {
        classroomId, slotIdx, batchSubjectId, dates,
      });
      const results = resp.results || [];
      const failed = results.filter((r) => !r.success);
      if (failed.length) {
        toast.error(`${dates.length - failed.length}/${dates.length} scheduled — ${failed.length} conflicted and were skipped`);
      } else {
        toast.success(`Duplicated to ${dates.length} date${dates.length > 1 ? 's' : ''}`);
      }
      // Keep the modal open so each date shows red (conflicted, skipped) or
      // green (scheduled) -- results is keyed by date for the render below.
      setDuplicateModal((prev) => (prev ? { ...prev, saving: false, results } : prev));
      silentRefreshWeek();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to duplicate');
      setDuplicateModal((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  }

  // "Who's viewing this page" -- joins/leaves a dedicated Socket.IO room for
  // as long as this tab stays open on this page.
  useEffect(() => {
    const user = AuthService.getUser();
    const staff = user?.superAdmin?.staff || {};
    const userId = user?.superAdmin?.superAdminId || staff.staffId;
    if (!userId) return undefined;

    NotificationService.connect(userId, 'admin', staff.name || user?.superAdmin?.username || 'Staff', staff.staffId);
    NotificationService.joinScheduleViewers({ staffId: staff.staffId, name: staff.name || 'Staff', imagePath: staff.imagePath || null });
    const unsubscribeViewers = NotificationService.onScheduleViewersUpdate((list) => setViewers(Array.isArray(list) ? list : []));
    const unsubscribeUpdates = NotificationService.onScheduleUpdated(() => silentRefreshWeek());

    return () => {
      NotificationService.leaveScheduleViewers();
      unsubscribeViewers();
      unsubscribeUpdates();
    };
  }, []);

  const visibleDays = useMemo(() => {
    if (!data?.days) return [];
    if (isNarrow) {
      const match = data.days.filter((d) => d.date === selectedDate);
      return match.length ? match : data.days.slice(0, 1);
    }
    if (!showTodayOnly) return data.days;
    const t = todayStr();
    const match = data.days.filter((d) => d.date === t);
    return match.length ? match : data.days;
  }, [data, showTodayOnly, isNarrow, selectedDate]);

  const totalClassrooms = useMemo(
    () => (data?.floors || []).reduce((sum, f) => sum + f.classrooms.length, 0),
    [data]
  );

  const dayIndexMap = useMemo(() => {
    if (!data?.days) return [];
    return visibleDays.map((d) => data.days.findIndex((dd) => dd.date === d.date));
  }, [data, visibleDays]);

  // Groups consecutive visible days by weekday/weekend so the header can show
  // a single merged "Weekday"/"Weekend" cell spanning that run of days.
  const dayGroups = useMemo(() => {
    const groups = [];
    visibleDays.forEach((d) => {
      const weekend = isWeekendDate(d.date);
      const last = groups[groups.length - 1];
      if (last && last.weekend === weekend) {
        last.count += 1;
      } else {
        groups.push({ weekend, count: 1 });
      }
    });
    return groups;
  }, [visibleDays]);

  const goToday = () => {
    const t = todayStr();
    setAnchor(t);
    setShowTodayOnly(true);
    setSelectedDate(t);
  };

  // Mobile date picker -- setting anchor to the picked date makes the backend
  // return the week containing it (data.days), which visibleDays then narrows
  // down to just that one date.
  function selectDate(dateStr) {
    if (!dateStr) return;
    setSelectedDate(dateStr);
    setAnchor(dateStr);
  }
  const shiftSelectedDate = (deltaDays) => selectDate(addDaysToAnchor(selectedDate, deltaDays));

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-5 py-3 shadow-sm">
        <h1 className="text-sm font-bold text-slate-800">🗓️ Classroom Weekly Schedule</h1>

        {permissions.canEdit ? (
          <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-700">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" /></svg>
            Edit Access
          </span>
        ) : (
          <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase text-slate-500">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
            View Only
          </span>
        )}

        {isNarrow ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => shiftSelectedDate(-1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => selectDate(e.target.value)}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600"
            />
            <button
              type="button"
              onClick={() => shiftSelectedDate(1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
            {selectedDate !== todayStr() && (
              <button
                type="button"
                onClick={goToday}
                className="rounded-lg bg-violet-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm"
              >
                Today
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setAnchor((a) => addDaysToAnchor(a, -7))}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <span className="min-w-[140px] text-center text-xs font-semibold text-slate-600">{data ? fmtWeekRange(data.days) : '—'}</span>
              <button
                type="button"
                onClick={() => setAnchor((a) => addDaysToAnchor(a, 7))}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>

            <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setShowTodayOnly(false)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${!showTodayOnly ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                This Week
              </button>
              <button
                type="button"
                onClick={goToday}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${showTodayOnly ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-500'}`}
              >
                Today
              </button>
            </div>
          </>
        )}

        <div className="ml-auto flex items-center gap-3">
          {viewers.length > 0 && (
            <div className="flex items-center">
              <div className="flex -space-x-2">
                {viewers.slice(0, 6).map((v, i) => (
                  <img
                    key={`${v.staffId || v.name}-${i}`}
                    src={v.imagePath ? getImageURL(v.imagePath) : getInitialsAvatar(v.name)}
                    alt={v.name}
                    title={v.name}
                    className="h-8 w-8 rounded-full border-2 border-white object-cover shadow-sm"
                  />
                ))}
              </div>
              {viewers.length > 6 && (
                <span className="ml-1 text-xs font-semibold text-slate-500">+{viewers.length - 6}</span>
              )}
              <span className="ml-2 hidden text-xs text-slate-400 sm:inline">viewing this page</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setLegendOpen(true)}
            title="What do the status colors mean?"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 sm:h-8 sm:w-8"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </button>
          <button
            type="button"
            onClick={() => window.close()}
            title="Close tab"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 sm:h-8 sm:w-8"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {legendOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setLegendOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">Schedule Guide</h2>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Afternoon Patterns</p>
            <div className="mb-4 space-y-2">
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
                <div className="mb-1 flex items-center gap-2">
                  <span className="rounded bg-sky-200 px-1.5 py-0.5 text-[9px] font-bold text-sky-800">Pattern A</span>
                  <span className="text-xs font-semibold text-sky-800">Two 2-hour blocks</span>
                </div>
                <p className="text-xs text-sky-700">1:00 PM – 3:00 PM · then · 3:00 PM – 5:00 PM</p>
                <p className="mt-0.5 text-[10px] text-sky-600">Classes run back-to-back from 1 PM with no lunch break.</p>
              </div>
              <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
                <div className="mb-1 flex items-center gap-2">
                  <span className="rounded bg-violet-200 px-1.5 py-0.5 text-[9px] font-bold text-violet-800">Pattern B</span>
                  <span className="text-xs font-semibold text-violet-800">Single block after lunch</span>
                </div>
                <p className="text-xs text-violet-700">1:00 PM – 2:00 PM lunch · then · 2:00 PM – 4:00 PM</p>
                <p className="mt-0.5 text-[10px] text-violet-600">A 1–2 PM lunch break then one afternoon class.</p>
              </div>
            </div>

            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Status Colors</p>
            <p className="mb-3 text-xs text-slate-500">
              These colors appear next to each batch/subject in the schedule and in the Batch No / Subject dropdowns.
            </p>
            <ul className="space-y-3">
              {STATUS_LEGEND.map((item) => (
                <li key={item.status} className="flex items-start gap-2.5">
                  <span className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT_CLASS[item.status] || 'bg-slate-300'}`} />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-slate-700">{item.label}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{item.appliesTo}</span>
                    </div>
                    <div className="text-xs text-slate-500">{item.description}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div
        className="flex-1 overflow-auto p-4"
        style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
        ref={scrollContainerRef}
        onScroll={handleTableScroll}
      >
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <svg className="h-10 w-10 animate-spin text-violet-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-90" stroke="currentColor" strokeWidth="3" strokeLinecap="round" d="M12 2a10 10 0 0 1 10 10" />
            </svg>
            <p className="text-sm font-semibold text-slate-500">Loading schedule…</p>
            <p className="text-xs text-slate-400">Fetching classrooms, sessions and availability</p>
          </div>
        ) : accessDenied ? (
          <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <svg className="h-10 w-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
            <h2 className="text-sm font-bold text-slate-700">Access Restricted</h2>
            <p className="text-xs text-slate-500">{error}</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">{error}</div>
        ) : !data || totalClassrooms === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-400">No classrooms found.</div>
        ) : (
          <table className="w-full min-w-[900px] border-collapse bg-white text-sm">
            <thead>
              <tr>
                <th
                  rowSpan={3}
                  style={{ position: stickyPos, left: FROZEN_COL_LEFT.timeSlot, width: TIME_SLOT_COL_WIDTH, minWidth: TIME_SLOT_COL_WIDTH, zIndex: 30 }}
                  className="border border-slate-300 bg-slate-200 px-3 py-2 text-xs font-bold uppercase text-slate-700 whitespace-nowrap"
                >
                  Time Slot
                </th>
                <th
                  rowSpan={3}
                  style={{ position: stickyPos, left: FROZEN_COL_LEFT.floor, width: FLOOR_COL_WIDTH, minWidth: FLOOR_COL_WIDTH, zIndex: 30 }}
                  className="border border-slate-300 bg-slate-200 px-3 py-2 text-xs font-bold uppercase text-slate-700 whitespace-nowrap"
                >
                  Floor
                </th>
                <th
                  rowSpan={3}
                  style={{
                    position: stickyPos,
                    left: FROZEN_COL_LEFT.location,
                    width: LOCATION_COL_WIDTH,
                    minWidth: LOCATION_COL_WIDTH,
                    zIndex: 30,
                    borderRight: '2px solid #94a3b8',
                    boxShadow: !isNarrow && isHScrolled ? FROZEN_EDGE_SHADOW : 'none',
                  }}
                  className="border border-slate-300 bg-slate-200 px-3 py-2 text-xs font-bold uppercase text-slate-700 whitespace-nowrap"
                >
                  Location
                </th>
                {dayGroups.map((g, i) => (
                  <th
                    key={i}
                    colSpan={g.count * (permissions.canEdit ? 5 : 4)}
                    className={`border border-slate-300 px-3 py-2 text-xs font-bold uppercase tracking-wide whitespace-nowrap ${
                      g.weekend ? 'bg-amber-200 text-amber-800' : 'bg-slate-300 text-slate-700'
                    }`}
                  >
                    {g.weekend ? 'Weekend' : 'Weekday'}
                  </th>
                ))}
              </tr>
              <tr>
                {visibleDays.map((d, i) => {
                  const isPast = d.date < todayStr();
                  const isToday = d.date === todayStr();
                  return (
                    <th
                      key={d.date}
                      colSpan={permissions.canEdit ? 5 : 4}
                      style={i > 0 ? { borderLeft: GROUP_BORDER } : undefined}
                      className={`border border-slate-300 px-3 py-2 text-xs font-bold uppercase whitespace-nowrap ${
                        isPast ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : isToday ? 'bg-sky-200 text-sky-800' : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {d.label} <span className="font-normal">({fmtDayHeader(d.date)})</span>
                      {isPast && <span className="ml-1.5 rounded-full bg-slate-300 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">PASSED</span>}
                      {isToday && <span className="ml-1.5 rounded-full bg-sky-500 px-1.5 py-0.5 text-[9px] font-bold text-white">TODAY</span>}
                    </th>
                  );
                })}
              </tr>
              <tr>
                {visibleDays.map((d, i) => {
                  const isPast = d.date < todayStr();
                  const isToday = d.date === todayStr();
                  const subHeaderClass = `border border-slate-300 px-2 py-1.5 text-xs font-semibold whitespace-nowrap ${
                    isPast ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : isToday ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'
                  }`;
                  const subHeaderStyle = { width: DAY_SUBCOL_WIDTH, minWidth: DAY_SUBCOL_WIDTH };
                  const firstSubHeaderStyle = i > 0 ? { ...subHeaderStyle, borderLeft: GROUP_BORDER } : subHeaderStyle;
                  const remarksHeaderStyle = { width: REMARKS_COL_WIDTH, minWidth: REMARKS_COL_WIDTH };
                  const actionsHeaderStyle = { width: ACTIONS_COL_WIDTH, minWidth: ACTIONS_COL_WIDTH };
                  return (
                    <React.Fragment key={d.date}>
                      <th style={firstSubHeaderStyle} className={subHeaderClass}>Batch No</th>
                      <th style={subHeaderStyle} className={subHeaderClass}>Subject</th>
                      <th style={subHeaderStyle} className={subHeaderClass}>Lecturer</th>
                      <th style={remarksHeaderStyle} className={subHeaderClass}>Remarks</th>
                      {permissions.canEdit && <th style={actionsHeaderStyle} className={subHeaderClass}>Actions</th>}
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.timeSlots.map((slotDef, slotIdx) => {
                // API returns { label, pattern } objects; guard against old string shape.
                const slotLabel = typeof slotDef === 'string' ? slotDef : slotDef.label;
                const slotPattern = typeof slotDef === 'string' ? null : slotDef.pattern;
                const pm = slotPattern ? PATTERN_META[slotPattern] : null;

                return (
                data.floors.map((floor, floorIdx) => (
                  floor.classrooms.map((classroom, classroomIdx) => (
                    <tr key={`${slotIdx}-${floor.floor}-${classroom.classroomId}`}>
                      {floorIdx === 0 && classroomIdx === 0 && (
                        <td
                          rowSpan={totalClassrooms}
                          style={{
                            position: stickyPos, left: FROZEN_COL_LEFT.timeSlot, width: TIME_SLOT_COL_WIDTH, minWidth: TIME_SLOT_COL_WIDTH, zIndex: 10,
                            ...(slotIdx > 0 ? { borderTop: GROUP_BORDER } : {}),
                          }}
                          className={`border border-slate-300 px-3 py-2 text-center align-middle text-xs font-bold text-slate-700 whitespace-nowrap ${
                            pm ? pm.slotBg : 'bg-slate-50'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span className={pm ? (slotPattern === 'A' ? 'text-sky-800' : 'text-violet-800') : ''}>{slotLabel}</span>
                            {pm && (
                              <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${pm.badgeCls}`}>{pm.badge}</span>
                            )}
                          </div>
                        </td>
                      )}
                      {classroomIdx === 0 && (
                        <td
                          rowSpan={floor.classrooms.length}
                          style={{
                            position: stickyPos, left: FROZEN_COL_LEFT.floor, width: FLOOR_COL_WIDTH, minWidth: FLOOR_COL_WIDTH, zIndex: 10,
                            ...(slotIdx > 0 && floorIdx === 0 ? { borderTop: GROUP_BORDER } : {}),
                          }}
                          className={`border border-slate-300 px-3 py-2 text-center align-middle text-xs font-semibold text-slate-700 whitespace-nowrap ${
                            pm ? pm.slotBg : 'bg-white'
                          }`}
                        >
                          {floor.floor}
                        </td>
                      )}
                      <td
                        style={{
                          position: stickyPos,
                          left: FROZEN_COL_LEFT.location,
                          width: LOCATION_COL_WIDTH,
                          minWidth: LOCATION_COL_WIDTH,
                          zIndex: 10,
                          borderRight: '2px solid #94a3b8',
                          boxShadow: !isNarrow && isHScrolled ? FROZEN_EDGE_SHADOW : 'none',
                          ...(slotIdx > 0 && floorIdx === 0 && classroomIdx === 0 ? { borderTop: GROUP_BORDER } : {}),
                        }}
                        className={`border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 whitespace-nowrap ${
                          pm ? pm.slotBg : 'bg-white'
                        }`}
                      >
                        {classroom.name}
                      </td>
                      {dayIndexMap.map((dayIdx, i) => {
                        const dateStr = visibleDays[i].date;
                        const originalCell = dayIdx >= 0 ? classroom.schedule[slotIdx][dayIdx] : null;
                        const key = cellKey(classroom.classroomId, slotIdx, dateStr);
                        const cell = getEffectiveCell(classroom.classroomId, slotIdx, dateStr, originalCell);
                        const isPast = dateStr < todayStr();
                        const isToday = dateStr === todayStr();
                        // A cell already holding another coordinator's batch (outside my
                        // coordinate-course scope) is fully locked -- I can still see it,
                        // but can't change or clear it. Empty cells stay editable for anyone
                        // with edit permission.
                        const isLockedByOtherCoordinator = !!cell?.batchId && !isBatchInMyScope(cell.batchId);
                        const canEditCell = permissions.canEdit && !isPast && !isLockedByOtherCoordinator;
                        const isOverScheduled =
                          cell?.numberOfClasses != null &&
                          cell?.scheduledCount != null &&
                          cell.numberOfClasses * HOURS_PER_CLASS - cell.scheduledCount * HOURS_PER_CLASS <= 0;
                        // Background priority: over-scheduled > pattern tint > lunch > today > past > default.
                        const bgClass = isOverScheduled ? 'bg-red-50'
                          : pm ? pm.cellBg
                          : isPast ? 'bg-slate-50/60'
                          : isToday ? 'bg-sky-50'
                          : '';
                        const textClass = isPast ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700';
                        const hoverCls = canEditCell ? (pm ? (slotPattern === 'A' ? 'hover:bg-sky-100 cursor-pointer' : 'hover:bg-violet-100 cursor-pointer') : 'hover:bg-violet-50 cursor-pointer') : '';
                        const cellClass = `border border-slate-200 px-2 py-2 text-xs ${bgClass} ${textClass} ${hoverCls}`;
                        const isNewSlotRow = slotIdx > 0 && floorIdx === 0 && classroomIdx === 0;
                        const topBorder = isNewSlotRow ? { borderTop: GROUP_BORDER } : {};
                        const leftBorder = i > 0 ? { borderLeft: GROUP_BORDER } : {};
                        const cellStyle = { width: DAY_SUBCOL_WIDTH, minWidth: DAY_SUBCOL_WIDTH, ...topBorder };
                        const firstCellStyle = { ...cellStyle, ...leftBorder };
                        const remarksStyle = { width: REMARKS_COL_WIDTH, minWidth: REMARKS_COL_WIDTH, ...topBorder };
                        const actionsStyle = { width: ACTIONS_COL_WIDTH, minWidth: ACTIONS_COL_WIDTH, ...topBorder };

                        // If an overlapping slot (cross-pattern conflict) already has a
                        // session in this classroom on this day, collapse all day columns
                        // into one merged cell so the slot can't be accidentally edited.
                        const overlapsWithSlot = slotDef?.overlapsWithSlot || [];
                        let blockingSlotIdx = -1;
                        let blockingCell = null;
                        if (dayIdx >= 0) {
                          for (const oi of overlapsWithSlot) {
                            const c = getEffectiveCell(
                              classroom.classroomId, oi, dateStr,
                              classroom.schedule[oi]?.[dayIdx] ?? null
                            );
                            if (c?.batchSubjectId) { blockingSlotIdx = oi; blockingCell = c; break; }
                          }
                        }
                        // Events that occupy this classroom+slot on this day block
                        // scheduling new classes (only when no class is already there).
                        const cellEvents = (!cell?.batchSubjectId && dayIdx >= 0)
                          ? (classroom.eventSchedule?.[slotIdx]?.[dayIdx] || [])
                          : [];
                        if (cellEvents.length > 0) {
                          const ev = cellEvents[0];
                          return (
                            <React.Fragment key={dayIdx}>
                              <td
                                colSpan={permissions.canEdit ? 5 : 4}
                                style={{ ...topBorder, ...leftBorder }}
                                className={`relative border border-slate-200 px-2 py-1.5 text-center align-middle ${isPast ? 'bg-slate-100/70' : isToday ? 'bg-sky-50/60' : 'bg-white'}`}
                              >
                                <div className="flex flex-col items-center gap-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span
                                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                                      style={{ backgroundColor: ev.eventTypeColor || '#6366f1' }}
                                    />
                                    <span className="text-[10px] font-semibold text-slate-700">
                                      {ev.title}
                                      {ev.eventTypeName && (
                                        <span className="ml-1 font-normal text-slate-400">({ev.eventTypeName})</span>
                                      )}
                                    </span>
                                  </div>
                                  {ev.staffNames && (
                                    <span className="text-[9px] text-slate-500" title={ev.staffNames}>{ev.staffNames}</span>
                                  )}
                                  {ev.hasStaffConflict && (
                                    <span
                                      className="mt-0.5 flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[9px] font-medium text-red-500"
                                      title={`Schedule conflict: ${(ev.conflictStaffNames || []).join(', ')} also has a class at this time`}
                                    >
                                      <svg className="h-2.5 w-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                      </svg>
                                      {(ev.conflictStaffNames || []).join(', ')} has a class
                                    </span>
                                  )}
                                  {cellEvents.length > 1 && (
                                    <span className="text-[9px] text-slate-300">+{cellEvents.length - 1} more</span>
                                  )}
                                </div>
                                <div className="absolute right-0.5 top-0.5 flex items-center gap-0.5">
                                  {ev.canClear && (
                                    <button
                                      type="button"
                                      title="Edit event"
                                      onClick={() => openEditEventModal({ ...ev, slotLabel: slotLabel }, classroom.name)}
                                      className="flex h-5 w-5 items-center justify-center rounded text-slate-300 hover:bg-violet-50 hover:text-violet-500"
                                    >
                                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487a2.1 2.1 0 1 1 2.97 2.97L7.5 19.79l-4 1 1-4 12.362-12.303z" />
                                      </svg>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    title="View event history"
                                    onClick={() => openEventHistoryModal(ev.eventId, ev.title)}
                                    className="flex h-5 w-5 items-center justify-center rounded text-slate-300 hover:bg-slate-100 hover:text-slate-500"
                                  >
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                                    </svg>
                                  </button>
                                  {ev.canClear && (
                                    <button
                                      type="button"
                                      title="Clear event"
                                      onClick={() => clearEvent(ev.eventId)}
                                      className="flex h-5 w-5 items-center justify-center rounded text-slate-300 hover:bg-red-50 hover:text-red-500"
                                    >
                                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </React.Fragment>
                          );
                        }

                        if (blockingCell) {
                          const bkSlotDef = data.timeSlots[blockingSlotIdx] || {};
                          const bkPm = bkSlotDef.pattern ? PATTERN_META[bkSlotDef.pattern] : null;
                          return (
                            <React.Fragment key={dayIdx}>
                              <td
                                colSpan={permissions.canEdit ? 5 : 4}
                                style={{ ...topBorder, ...leftBorder }}
                                className={`border border-dashed border-slate-300 px-2 py-1.5 text-center align-middle ${isPast ? 'bg-slate-100/70' : isToday ? 'bg-sky-50/60' : 'bg-slate-50'}`}
                              >
                                <div className="flex flex-col items-center gap-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <svg className="h-3 w-3 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                    {bkPm && (
                                      <span className={`rounded px-1 py-0.5 text-[9px] font-bold ${bkPm.badgeCls}`}>{bkPm.badge}</span>
                                    )}
                                    <span className="text-[10px] font-semibold text-slate-500">{bkSlotDef.label}</span>
                                  </div>
                                  {(blockingCell.batchNo || blockingCell.subject) && (
                                    <p className="text-[10px] text-slate-400 leading-tight">
                                      {blockingCell.batchNo}{blockingCell.subject ? ` · ${blockingCell.subject}` : ''} occupies this time
                                    </p>
                                  )}
                                </div>
                              </td>
                            </React.Fragment>
                          );
                        }

                        return (
                          <React.Fragment key={dayIdx}>
                            <td
                              style={firstCellStyle}
                              className={cellClass}
                              onClick={() => canEditCell && setEditingCell({ key, column: 'batch' })}
                            >
                              {editingCell?.key === key && editingCell.column === 'batch' ? (
                                <SearchableCellSelect
                                  value={cell?.batchId || ''}
                                  placeholder="Search batch…"
                                  onClose={() => setEditingCell(null)}
                                  onSelect={(val) => handleBatchChange(key, val)}
                                  options={editableBatches.map((b) => ({
                                    value: b.batchId,
                                    label: b.batchName + (b.courseShortName ? ` (${b.courseShortName})` : ''),
                                    status: b.status,
                                  }))}
                                />
                              ) : cell?.batchNo ? (
                                <div>
                                  <span className="flex items-center">
                                    <StatusDot status={getBatchStatus(cell.batchId)} />
                                    {cell.batchNo}
                                    {canEditCell && !cell.batchSubjectId && (
                                      <button
                                        type="button"
                                        title="Clear unsaved batch selection"
                                        aria-label="Clear unsaved batch selection"
                                        onClick={(e) => { e.stopPropagation(); clearPendingBatch(key); }}
                                        className="ml-1 shrink-0 rounded text-slate-400 hover:bg-slate-200 hover:text-red-600"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                  {cell.hasConflict && cell.conflictNotes?.length > 0 && (
                                    <span
                                      className="mt-1 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-700"
                                      title={cell.conflictNotes.join('\n')}
                                    >
                                      Conflict
                                    </span>
                                  )}
                                </div>
                              ) : (
                                canEditCell && (
                                  <span className={`italic ${pm ? pm.emptyHint : 'text-slate-300'}`}>
                                    {pm ? `+ batch (${pm.badge})` : '+ batch'}
                                  </span>
                                )
                              )}
                            </td>
                            <td
                              style={cellStyle}
                              className={cellClass}
                              onClick={() => {
                                if (!canEditCell || !cell?.batchId || loadingCell === key) return;
                                handleSubjectCellClick(key, cell.batchId, dateStr, slotIdx, classroom.classroomId);
                              }}
                            >
                              {loadingCell === key ? (
                                <div className="flex items-center justify-center py-0.5">
                                  <svg className="h-4 w-4 animate-spin text-violet-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                </div>
                              ) : editingCell?.key === key && editingCell.column === 'subject' ? (
                                <SearchableCellSelect
                                  value={cell?.batchSubjectId || ''}
                                  placeholder="Search subject…"
                                  onClose={() => setEditingCell(null)}
                                  onSelect={(val) => handleSubjectChange(key, classroom.classroomId, slotIdx, dateStr, cell.batchId, val, classroom.name, slotLabel)}
                                  options={(batchSubjectsCache[cell.batchId] || [])
                                    .map((s) => {
                                      const avail = subjectAvailabilityCache[`${classroom.classroomId}-${cell.batchId}-${dateStr}-${slotIdx}`]?.[s.batchSubjectId];
                                      return {
                                        value: s.batchSubjectId,
                                        label: `${s.subjectShortName} - ${s.staffName || 'No lecturer'}`,
                                        status: s.status,
                                        hasLecturer: !!s.staffName,
                                        available: avail?.available ?? null,
                                        availabilityReason: avail?.reason ?? null,
                                      };
                                    })
                                    // Primary: subject status, ongoing first, then not started, then completed.
                                    // Secondary (within each status): available lecturer, then busy lecturer,
                                    // then no lecturer assigned at all.
                                    .sort((a, b) => {
                                      const STATUS_ORDER = { ongoing: 0, 'not started': 1, completed: 2 };
                                      const statusRank = (o) => STATUS_ORDER[o.status] ?? 3;
                                      if (statusRank(a) !== statusRank(b)) return statusRank(a) - statusRank(b);
                                      const availRank = (o) => (!o.hasLecturer ? 2 : (o.available === false ? 1 : 0));
                                      return availRank(a) - availRank(b);
                                    })}
                                />
                              ) : cell?.subject ? (
                                <div>
                                  <span className="flex items-center">
                                    <StatusDot status={getSubjectStatus(cell.batchId, cell.batchSubjectId)} />
                                    {cell.subject}
                                  </span>
                                  <HoursBadge numberOfClasses={cell.numberOfClasses} scheduledCount={cell.scheduledCount} />
                                </div>
                              ) : (
                                canEditCell && cell?.batchId && <span className="italic text-slate-300">+ subject</span>
                              )}
                            </td>
                            <td style={cellStyle} className={cellClass.replace('cursor-pointer', '').replace(/hover:bg-\S+/, '')}>
                              {cell?.lecturer ? (
                                <span className="flex items-center gap-1">
                                  <span
                                    title={cell.hasConflict ? (cell.conflictNotes?.[0] || 'Schedule conflict') : 'No conflicts'}
                                    className={`h-2 w-2 shrink-0 rounded-full ${cell.hasConflict ? 'bg-red-400' : 'bg-emerald-400'}`}
                                  />
                                  {cell.lecturer}
                                </span>
                              ) : (
                                cell?.batchSubjectId ? <span className="italic text-slate-300">No lecturer assigned</span> : ''
                              )}
                            </td>
                            <td style={remarksStyle} className={`${cellClass} whitespace-normal`}>
                              {cell?.remarks || '-'}
                            </td>
                            {permissions.canEdit && (
                              <td style={actionsStyle} className={cellClass.replace('cursor-pointer', '').replace(/hover:bg-\S+/, '') + ' relative text-center'}>
                                {cell?.batchSubjectId ? (() => {
                                  // canEditCell already excludes out-of-scope batches (see
                                  // isLockedByOtherCoordinator above), so it alone is enough here.
                                  const canManageThisCell = canEditCell;
                                  return (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => setActionsMenuKey(actionsMenuKey === key ? null : key)}
                                        className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                                        title="Actions"
                                      >
                                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
                                      </button>
                                      {actionsMenuKey === key && (
                                        <div
                                          className="absolute right-1 top-8 z-50 w-36 rounded-lg border border-slate-200 bg-white py-1 text-left text-xs shadow-lg"
                                          onMouseLeave={() => setActionsMenuKey(null)}
                                        >
                                          {canManageThisCell && (
                                            <button
                                              type="button"
                                              onClick={() => handleClearCell(key, classroom.classroomId, slotIdx, dateStr, `${cell.batchNo}${cell.subject ? ` - ${cell.subject}` : ''}`)}
                                              className="block w-full px-3 py-1.5 text-left font-medium text-red-600 hover:bg-red-50"
                                            >
                                              Clear
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => openHistoryModal(`${classroom.name} · ${slotLabel} · ${dateStr}`, classroom.classroomId, slotIdx, dateStr)}
                                            className="block w-full px-3 py-1.5 text-left font-medium text-slate-700 hover:bg-slate-50"
                                          >
                                            View History
                                          </button>
                                          {canManageThisCell && (
                                            <button
                                              type="button"
                                              onClick={() => openDuplicateModal(`${cell.batchNo} · ${cell.subject}`, classroom.classroomId, slotIdx, cell.batchSubjectId)}
                                              className="block w-full px-3 py-1.5 text-left font-medium text-slate-700 hover:bg-slate-50"
                                            >
                                              Duplicate
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            disabled
                                            className="block w-full px-3 py-1.5 text-left font-medium text-slate-300 cursor-not-allowed"
                                            title="Clear the class schedule first before adding an event"
                                          >
                                            Add Event
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  );
                                })() : canEditCell ? (
                                  <div className="relative">
                                    <button
                                      type="button"
                                      onClick={() => setActionsMenuKey(actionsMenuKey === key ? null : key)}
                                      className="rounded p-1 text-slate-300 hover:bg-violet-50 hover:text-violet-500"
                                      title="Actions"
                                    >
                                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
                                    </button>
                                    {actionsMenuKey === key && (
                                      <div
                                        className="absolute right-1 top-8 z-50 w-36 rounded-lg border border-slate-200 bg-white py-1 text-left text-xs shadow-lg"
                                        onMouseLeave={() => setActionsMenuKey(null)}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => { setActionsMenuKey(null); openEventModal(classroom, slotDef, dateStr); }}
                                          className="block w-full px-3 py-1.5 text-left font-medium text-violet-600 hover:bg-violet-50"
                                        >
                                          Add Event
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => { setActionsMenuKey(null); openHistoryModal(`${classroom.name} · ${slotLabel} · ${dateStr}`, classroom.classroomId, slotIdx, dateStr); }}
                                          className="block w-full px-3 py-1.5 text-left font-medium text-slate-700 hover:bg-slate-50"
                                        >
                                          View History
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))
                ))
              );
              })}
            </tbody>
          </table>
        )}
      </div>

      {duplicateModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setDuplicateModal(null)}
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">Duplicate Schedule</h2>
              <button
                type="button"
                onClick={() => setDuplicateModal(null)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              Copies <span className="font-semibold text-slate-700">{duplicateModal.label}</span> to the dates you add below.
            </p>
            <div className="mb-3 flex items-center gap-2">
              <input
                type="date"
                value={duplicateModal.pickerValue}
                onChange={(e) => setDuplicateModal((prev) => ({ ...prev, pickerValue: e.target.value }))}
                className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-violet-400"
              />
              <button
                type="button"
                onClick={() => addDuplicateDate(duplicateModal.pickerValue)}
                className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
              >
                Add
              </button>
            </div>
            {duplicateModal.dates.length > 0 && (
              <ul className="mb-4 max-h-40 space-y-1 overflow-y-auto">
                {duplicateModal.dates.map((d) => {
                  const result = duplicateModal.results?.find((r) => r.date === d);
                  const rowClass = !result
                    ? 'bg-slate-50 text-slate-700'
                    : result.success
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border border-red-200 bg-red-50 text-red-700';
                  return (
                    <li key={d} className={`rounded px-2.5 py-1.5 text-xs ${rowClass}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{d}</span>
                        <div className="flex items-center gap-2">
                          {result && (
                            <span className="text-[10px] font-bold uppercase">{result.success ? '✓ Scheduled' : 'Conflict'}</span>
                          )}
                          <button type="button" onClick={() => removeDuplicateDate(d)} className="text-slate-400 hover:text-red-500">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </div>
                      {result && !result.success && (
                        <div className="mt-1 space-y-0.5">
                          {(result.warnings || []).map((w, i) => (
                            <div key={i}>{w.message}</div>
                          ))}
                          {result.message && !result.warnings && <div>{result.message}</div>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={duplicateModal.saving}
                onClick={() => setDuplicateModal(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                {duplicateModal.results ? 'Close' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={!duplicateModal.dates.length || duplicateModal.saving}
                onClick={saveDuplicate}
                className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {duplicateModal.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryModal(null)}
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">Change History</h2>
              <button
                type="button"
                onClick={() => setHistoryModal(null)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-500">{historyModal.label}</p>
            {historyModal.loading ? (
              <div className="py-6 text-center text-xs text-slate-400">Loading…</div>
            ) : historyModal.entries.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">
                No history yet for this slot.
              </div>
            ) : (
              <ul className="max-h-80 space-y-2 overflow-y-auto">
                {historyModal.entries.map((h, i) => (
                  <li key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                          h.action === 'cleared' ? 'bg-red-100 text-red-600' : h.action === 'created' ? 'bg-emerald-100 text-emerald-600' : 'bg-sky-100 text-sky-600'
                        }`}
                      >
                        {h.action}
                      </span>
                      <span className="text-slate-400">{new Date(h.changedAt).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 text-slate-700">
                      {h.batchNo}{h.subject ? ` — ${h.subject}` : ''}{h.staffName ? ` (${h.staffName})` : ''}
                    </div>
                    <div className="mt-0.5 text-slate-500">by {h.changedByName || 'Unknown'}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {requiredFixModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !requiredFixModal.saving && setRequiredFixModal(null)}
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">Needs Setup First</h2>
              <button
                type="button"
                onClick={() => !requiredFixModal.saving && setRequiredFixModal(null)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{requiredFixModal.batchName}</span> — {requiredFixModal.subjectName} isn't ready to be scheduled yet:
            </p>
            <ul className="mb-3 space-y-1 text-xs text-amber-700">
              {requiredFixModal.missingLecturer && <li>• No lecturer is assigned to this subject.</li>}
              {requiredFixModal.notStarted && <li>• This subject's status is still "Not Started".</li>}
            </ul>

            {requiredFixModal.missingLecturer && (
              <div className="mb-3">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Assign Lecturer</label>
                <Select
                  options={activeStaff.map((s) => ({ value: s.staffId, label: s.name }))}
                  value={
                    activeStaff
                      .map((s) => ({ value: s.staffId, label: s.name }))
                      .find((o) => String(o.value) === String(requiredFixModal.staffIdInput)) || null
                  }
                  onChange={(opt) => setRequiredFixModal((prev) => ({ ...prev, staffIdInput: opt ? opt.value : '' }))}
                  placeholder="Search lecturer…"
                  isClearable
                  classNamePrefix="rs"
                  styles={{ control: (base) => ({ ...base, minHeight: '32px', fontSize: '0.75rem' }) }}
                />
              </div>
            )}

            {requiredFixModal.notStarted && (
              <div className="mb-4">
                <label className="mb-1 block text-xs font-semibold text-slate-600">Subject Status</label>
                <button
                  type="button"
                  onClick={() => setRequiredFixModal((prev) => ({ ...prev, statusInput: 'ongoing' }))}
                  className={`w-full rounded border px-2 py-1.5 text-xs font-semibold ${
                    requiredFixModal.statusInput === 'ongoing'
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {requiredFixModal.statusInput === 'ongoing' ? '✓ Marked as Ongoing' : 'Mark as Ongoing'}
                </button>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={requiredFixModal.saving}
                onClick={() => setRequiredFixModal(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={requiredFixModal.saving}
                onClick={confirmRequiredFix}
                className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {requiredFixModal.saving ? 'Saving…' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {scheduleConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !scheduleConfirm.saving && setScheduleConfirm(null)}
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">Confirm Schedule</h2>
              <button
                type="button"
                onClick={() => !scheduleConfirm.saving && setScheduleConfirm(null)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-slate-400">Batch</dt>
              <dd className="font-semibold text-slate-700">{scheduleConfirm.batchName}</dd>
              <dt className="text-slate-400">Subject</dt>
              <dd className="font-semibold text-slate-700">{scheduleConfirm.subjectName}</dd>
              <dt className="text-slate-400">Lecturer</dt>
              <dd className="font-semibold text-slate-700">{scheduleConfirm.lecturerName || 'Not assigned'}</dd>
              <dt className="text-slate-400">Classroom</dt>
              <dd className="font-semibold text-slate-700">{scheduleConfirm.classroomName}</dd>
              <dt className="text-slate-400">Date</dt>
              <dd className="font-semibold text-slate-700">{scheduleConfirm.dateStr}</dd>
              <dt className="text-slate-400">Time</dt>
              <dd className="font-semibold text-slate-700">{scheduleConfirm.slotLabel}</dd>
            </dl>

            <p className="mb-2 text-xs font-semibold text-slate-600">Before you schedule this, please note:</p>
            <ul className="mb-4 max-h-52 space-y-1.5 overflow-y-auto">
              {scheduleConfirm.warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>
                  {w.message}
                </li>
              ))}
            </ul>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={scheduleConfirm.saving}
                onClick={() => setScheduleConfirm(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={scheduleConfirm.saving}
                onClick={confirmSchedule}
                className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {scheduleConfirm.saving ? 'Saving…' : 'Confirm & Schedule Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {eventModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !savingEvent && setEventModal(null)}
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-800">{eventModal.eventId ? 'Edit Event' : 'Add Event'}</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {eventModal.classroomName} · {eventModal.slotLabel || eventModal.dateStr}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !savingEvent && setEventModal(null)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  maxLength={255}
                  placeholder="e.g. Mock exam invigilating"
                  value={eventForm.title}
                  onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Event Type <span className="text-red-500">*</span></label>
                <select
                  value={eventForm.eventTypeId ?? ''}
                  onChange={(e) => setEventForm((f) => ({ ...f, eventTypeId: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                >
                  <option value="">Select type…</option>
                  {eventTypes.filter((t) => t.isActive !== 0).map((t) => (
                    <option key={t.eventTypeId} value={t.eventTypeId}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Start</label>
                  <input
                    type="time"
                    value={eventModal.startTime}
                    onChange={(e) => setEventModal((m) => ({ ...m, startTime: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-semibold text-slate-600">End</label>
                  <input
                    type="time"
                    value={eventModal.endTime}
                    onChange={(e) => setEventModal((m) => ({ ...m, endTime: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                  />
                </div>
              </div>
              {(() => {
                if (!eventModal.startTime || !eventModal.endTime) return null;
                const [sh, sm] = eventModal.startTime.split(':').map(Number);
                const [eh, em] = eventModal.endTime.split(':').map(Number);
                const mins = (eh * 60 + em) - (sh * 60 + sm);
                if (mins <= 0) return (
                  <p className="text-[10px] font-medium text-red-500">End time must be after start time</p>
                );
                const h = Math.floor(mins / 60), m = mins % 60;
                const label = [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ');
                return <p className="text-[10px] text-slate-400">Duration: <span className="font-semibold text-slate-600">{label}</span></p>;
              })()}

              <div>
                <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                  Assign Staff
                  {loadingStaffAvail && (
                    <svg className="h-3 w-3 animate-spin text-violet-400" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                </label>
                <div className="max-h-36 overflow-y-auto rounded-lg border border-slate-200 p-2">
                  {activeStaff.length === 0 ? (
                    <p className="text-xs text-slate-400">Loading staff…</p>
                  ) : (
                    activeStaff.map((s) => {
                      const avail = staffEventAvailability[s.staffId];
                      const isAvail = avail?.available ?? null;
                      const reason = avail?.reason || null;
                      return (
                        <label key={s.staffId} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-slate-50">
                          <input
                            type="checkbox"
                            className="accent-violet-600"
                            checked={eventForm.staffIds.includes(s.staffId)}
                            onChange={(e) => setEventForm((f) => ({
                              ...f,
                              staffIds: e.target.checked
                                ? [...f.staffIds, s.staffId]
                                : f.staffIds.filter((id) => id !== s.staffId),
                            }))}
                          />
                          <span className="flex-1 text-slate-700">{s.name}</span>
                          {isAvail !== null && (
                            <span
                              title={isAvail ? 'Available' : (reason || 'Not available')}
                              className={`h-2 w-2 shrink-0 rounded-full ${isAvail ? 'bg-emerald-400' : 'bg-red-400'}`}
                            />
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Notes</label>
                <textarea
                  rows={2}
                  placeholder="Optional notes…"
                  value={eventForm.notes}
                  onChange={(e) => setEventForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={savingEvent}
                onClick={() => setEventModal(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingEvent}
                onClick={saveEvent}
                className="rounded-lg bg-violet-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {savingEvent ? (eventModal.eventId ? 'Updating…' : 'Adding…') : (eventModal.eventId ? 'Update Event' : 'Add Event')}
              </button>
            </div>
          </div>
        </div>
      )}

      {eventHistoryModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setEventHistoryModal(null)}
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-800">Event History</h2>
                <p className="mt-0.5 text-xs text-slate-500">{eventHistoryModal.label}</p>
              </div>
              <button
                type="button"
                onClick={() => setEventHistoryModal(null)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {eventHistoryModal.loading ? (
              <div className="flex items-center justify-center py-8">
                <svg className="h-5 w-5 animate-spin text-violet-400" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : eventHistoryModal.entries.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-400">No history recorded yet.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {eventHistoryModal.entries.map((h, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                        h.action === 'created' ? 'bg-emerald-100 text-emerald-700' :
                        h.action === 'cleared' ? 'bg-red-100 text-red-600' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {h.action.charAt(0).toUpperCase() + h.action.slice(1)}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {h.changedByName} · {new Date(h.changedAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-slate-600">
                      {h.title && <p><span className="font-medium">Title:</span> {h.title}</p>}
                      {h.eventTypeName && <p><span className="font-medium">Type:</span> {h.eventTypeName}</p>}
                      {(h.startTime || h.endTime) && (
                        <p><span className="font-medium">Time:</span> {h.startTime} – {h.endTime}</p>
                      )}
                      {h.staffNames && <p><span className="font-medium">Staff:</span> {h.staffNames}</p>}
                      {h.notes && <p><span className="font-medium">Notes:</span> {h.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar theme="colored" />
    </div>
  );
}
